/**
 * Auto-deduplication middleware
 * Automatically triggers client-side deduplication after new AI analysis data is appended
 * Also triggers geocoding for locations with pending status
 * Uses debouncing to avoid excessive processing
 */

import { Middleware } from '@reduxjs/toolkit';
import { deduplicateAiAnalysisData, geocodePendingLocations } from '../slices/aiAnalysisSlice';
import logger from '@/lib/logger';

const DEBOUNCE_DELAY = 3000; // 3 seconds
const GEOCODING_DELAY = 5000; // 5 seconds (give entities time to settle)

export const autoDeduplicationMiddleware: Middleware = (store) => {
  let deduplicationTimer: NodeJS.Timeout | null = null;
  let geocodingTimer: NodeJS.Timeout | null = null;

  return (next) => (action) => {
  // Let the action pass through first
  const result = next(action);

  // Check if this is an appendAiAnalysisPayload or hydrateAiAnalysisState action
  if (typeof action === 'object' && action !== null && 'type' in action &&
      (action.type === 'aiAnalysis/appendAiAnalysisPayload' ||
       action.type === 'aiAnalysis/hydrateAiAnalysisState')) {
    logger.info('AutoDeduplicationMiddleware', 'Data appended, scheduling deduplication...');

    // Clear existing timer
    if (deduplicationTimer) {
      clearTimeout(deduplicationTimer);
    }

    // Schedule deduplication after debounce delay
    deduplicationTimer = setTimeout(() => {
      logger.info('AutoDeduplicationMiddleware', 'Triggering client-side deduplication...');
      store.dispatch(deduplicateAiAnalysisData() as any);
      deduplicationTimer = null;
    }, DEBOUNCE_DELAY);

    // Schedule geocoding after a longer delay (entities need to settle first)
    if (geocodingTimer) {
      clearTimeout(geocodingTimer);
    }
    geocodingTimer = setTimeout(() => {
      const state = store.getState() as any;
      const locations = state.aiAnalysis?.entities?.locations || [];
      const hasPending = locations.some((loc: any) => loc.geocodingStatus === 'pending');
      if (hasPending) {
        logger.info('AutoDeduplicationMiddleware', 'Triggering geocoding for pending locations...');
        store.dispatch(geocodePendingLocations() as any);
      }
      geocodingTimer = null;
    }, GEOCODING_DELAY);
  }

  return result;
  };
};
